import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { getConfig } from '../services/config';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');
  const config = getConfig();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token provided. Please check the link in your email.');
        return;
      }

      try {
        const response = await fetch(
          `${config.apiBaseUrl}/api/v2/customers/verify-email?token=${encodeURIComponent(token)}`,
          {
            method: 'POST',
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail || `Verification failed: ${response.statusText}`
          );
        }

        await response.json();
        setStatus('success');
        setMessage('✓ Email verified successfully! Redirecting...');

        // Clear cached permissions to force refresh
        localStorage.removeItem('userPermissions');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          // Force a full page reload to refresh auth state
          window.location.href = '/';
        }, 2000);
      } catch (err: any) {
        setStatus('error');
        setMessage(
          err.message ||
          'Failed to verify email. The link may have expired. Please sign up again.'
        );
      }
    };

    verifyEmail();
  }, [searchParams, navigate, config.apiBaseUrl]);

  return (
    <Container maxWidth="sm">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 3 }}>
              {status === 'loading' && (
                <CircularProgress size={60} sx={{ color: '#667eea' }} />
              )}
              {status === 'success' && (
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
              )}
              {status === 'error' && (
                <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />
              )}
            </Box>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {status === 'loading' && 'Verifying Email'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </Typography>

            <Alert severity={status === 'error' ? 'error' : 'info'} sx={{ mb: 2 }}>
              {message}
            </Alert>

            {status === 'error' && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/signup')}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  Sign Up Again
                </Button>
                <Button variant="outlined" onClick={() => navigate('/')}>
                  Back Home
                </Button>
              </Box>
            )}

            {status === 'success' && (
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  mt: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Go to Dashboard
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
